import React from 'react';
import { Clock, Plus } from 'lucide-react';
import Badge from './Badge';
import Button from './Button';
import Card from './Card';
import type { BusinessIntegration } from '../../types';

interface IntegrationCardProps {
  integration: BusinessIntegration;
  onConnect?: () => void;
  isLoading?: boolean;
  className?: string;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  onConnect,
  isLoading = false,
  className = '',
}) => {
  const { name, logo, status } = integration;
  const isDisabled = status === 'coming_soon';

  const getStatusBadge = (): React.ReactNode => {
    if (status === 'coming_soon') {
      return (
        <Badge variant="warning" size="sm">
          <Clock className="w-3 h-3 mr-1" />
          Soon
        </Badge>
      );
    }
    if (status === 'beta') {
      return (
        <Badge variant="info" size="sm">
          Beta
        </Badge>
      );
    }
    return null;
  };

  const handleConnectClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!isDisabled && onConnect) {
      onConnect();
    }
  };

  return (
    <Card
      padding="none"
      className={`
        p-5 transition-all duration-200
        ${isDisabled ? 'opacity-45 cursor-default' : 'cursor-pointer hover:border-input hover:-translate-y-0.5 hover:shadow-lg'}
        ${className}
      `}
      onClick={handleConnectClick}
    >
      <div className="flex items-start justify-between mb-3.5">
        <div className="w-14 h-14 rounded-xl bg-muted/50 border border-border flex items-center justify-center overflow-hidden flex-shrink-0">
          <img
            src={logo}
            alt={`${name} logo`}
            className="w-9 h-9 object-contain"
          />
        </div>
        {getStatusBadge()}
      </div>

      <h3 className="text-[15px] font-semibold text-foreground mb-3.5">
        {name}
      </h3>

      {status === 'available' ? (
        <Button
          onClick={handleConnectClick}
          disabled={isLoading}
          isLoading={isLoading}
          className="w-full"
          variant="primary"
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Connect
        </Button>
      ) : (
        <Button disabled className="w-full" variant="secondary">
          Coming Soon
        </Button>
      )}
    </Card>
  );
};

export default IntegrationCard;
